import { io } from 'socket.io-client';

/**
 * socket.io wrapper for Conversations.
 *
 * Polling is kept in the transport list on purpose: the chat service sits behind
 * Apache, and if mod_proxy_wstunnel is missing or a member's network strips the
 * Upgrade header, chat degrades to long-polling instead of failing outright.
 */

const SOCKET_URL = import.meta.env.VITE_CHAT_SOCKET_URL || undefined; // same origin by default

export function createChatSocket({ onEvent, onStatus }) {
  const socket = io(SOCKET_URL, {
    path: '/chat/ws',
    withCredentials: true,
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 500,
    reconnectionDelayMax: 10000,
    randomizationFactor: 0.5,
  });

  const forward = (event) => socket.on(event, (payload) => onEvent(event, payload));

  [
    'message:new',
    'message:edit',
    'message:delete',
    'reaction:add',
    'reaction:remove',
    'typing:start',
    'typing:stop',
    'unread:update',
    'conversation:new',
    'conversation:removed',
    'conversation:activity',
    'pin:add',
    'pin:remove',
  ].forEach(forward);

  socket.on('ready', (payload) => onStatus({ state: 'ready', ...payload }));
  socket.on('connect', () => onStatus({ state: 'connected' }));
  socket.on('disconnect', (reason) => onStatus({ state: 'disconnected', reason }));
  socket.on('connect_error', (error) => onStatus({ state: 'error', error: error.message }));

  // Member tokens last 30 days with no refresh flow, so the server closes the
  // socket at expiry. Reconnecting is pointless until the member logs in again.
  socket.on('token_expired', () => {
    onStatus({ state: 'expired' });
    socket.disconnect();
  });

  return {
    socket,
    /** Promise-wrapped emit — every server handler acks {success, data|error}. */
    request: (event, payload) =>
      new Promise((resolve, reject) => {
        socket.timeout(10000).emit(event, payload, (timeoutError, ack) => {
          if (timeoutError) return reject(new Error('Request timed out'));
          if (!ack?.success) return reject(Object.assign(new Error(ack?.error || 'Request failed'), {
            status: ack?.statusCode,
          }));
          return resolve(ack.data);
        });
      }),
    emit: (event, payload) => socket.emit(event, payload),
    close: () => socket.close(),
  };
}

export default createChatSocket;
